/**
 * The app's date formats, in one place. About twenty files used to define a
 * local `formatDate`, most producing "Sep 21, 2026" but each with its own
 * locale (`undefined` here, `en-US` there), its own timezone handling and its
 * own empty-value fallback. Locale is fixed to en-US so a server-rendered date
 * matches the client's and never hydrates differently.
 *
 * All formatters accept a Date, an ISO string or a timestamp, plus a `config`
 * of `{ fallback, timeZone, timeZoneName }` (not Intl options), and return
 * `fallback` (default "") for a missing or unparseable value.
 *
 * `timeZone` matters for date-only values: a due date stored at UTC midnight
 * reads a day early in the Americas unless it is formatted in UTC. Pass
 * `{ timeZone: "UTC" }` for those; leave it off for real instants (created,
 * completed, sent) which should read in the viewer's zone. For a due date the
 * learner is meant to act on, prefer `<LocalDueDate>` / `lib/due-date.js`.
 *
 * Emails have their own formatters in `lib/email/*` (same-year variants and a
 * recipient timezone); do not reach for these there.
 */
const LOCALE = "en-US";

function toDate(value) {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatWith(value, parts, { fallback = "", timeZone, timeZoneName } = {}) {
  const date = toDate(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat(LOCALE, {
    ...parts,
    ...(timeZone ? { timeZone } : {}),
    ...(timeZoneName ? { timeZoneName } : {}),
  }).format(date);
}

/** "Sep 21, 2026" */
export function formatShortDate(value, config) {
  return formatWith(value, { month: "short", day: "numeric", year: "numeric" }, config);
}

/** "Sep 21" */
export function formatMonthDay(value, config) {
  return formatWith(value, { month: "short", day: "numeric" }, config);
}

/** "September 21, 2026" */
export function formatLongDate(value, config) {
  return formatWith(value, { month: "long", day: "numeric", year: "numeric" }, config);
}

/** "Sep 21, 2026, 3:04 PM" — add `timeZoneName: "short"` for "… PM EDT". */
export function formatDateTime(value, config) {
  return formatWith(
    value,
    { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" },
    config,
  );
}

/** "3:04 PM" */
export function formatTime(value, config) {
  return formatWith(value, { hour: "numeric", minute: "2-digit" }, config);
}
